import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { confirmAlert } from 'react-confirm-alert';
import SelectOption from '../General/SelectOption';
import { previewCreatorCommissionVoid, voidCreatorCommissions } from '../../api/affiliate_commissions';
import { formatAmountInCurrency, pluralizeCount } from '../../helpers/formatting';
import './VoidCreatorCommissions.scss';

const VoidCreatorCommissions = ({ brandOptions, searchForCreator }) => {
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [reason, setReason] = useState('');
  const [includeInvoiced, setIncludeInvoiced] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const summary = preview?.summary;
  const totalCount = summary ? summary.uninvoiced.count + summary.invoiced.count + summary.paidOut.count : 0;
  const voidCount = summary ? summary.uninvoiced.count + (includeInvoiced ? summary.invoiced.count : 0) : 0;
  const voidTotalUsd = summary ? summary.uninvoiced.totalUsd + (includeInvoiced ? summary.invoiced.totalUsd : 0) : 0;
  const invoiceLabels = preview ? preview.invoices.map(invoice => `#${invoice.id}`).join(', ') : '';
  const scopeLabel = preview?.brand ? `at ${preview.brand.name}` : 'across all brands';
  const countTiles = summary
    ? [
        { label: 'Will void', count: voidCount, totalUsd: voidTotalUsd, className: 'void' },
        { label: 'Invoiced', count: summary.invoiced.count, totalUsd: summary.invoiced.totalUsd },
        { label: 'Already paid out', count: summary.paidOut.count, totalUsd: summary.paidOut.totalUsd }
      ]
    : [];
  const canPreview = !!selectedCreator && !isPreviewing && !isVoiding;
  const canVoid = voidCount > 0 && !!reason.trim() && !isPreviewing && !isVoiding;

  const selectCreator = creator => {
    setSelectedCreator(creator);
    setPreview(null);
  };

  const selectBrand = brand => {
    setSelectedBrand(brand);
    setPreview(null);
  };

  const handlePreview = () => {
    if (!canPreview) return;
    setIsPreviewing(true);
    setLastResult(null);
    previewCreatorCommissionVoid({ User_id: selectedCreator.value, Brand_id: selectedBrand?.value })
      .then(result => {
        setPreview(result);
        setIncludeInvoiced(false);
      })
      .catch(err => cogoToast.error(err || 'Failed to load the creator’s commissions.'))
      .finally(() => setIsPreviewing(false));
  };

  const submitVoid = () => {
    setIsVoiding(true);
    const creatorName = preview.user.name;
    voidCreatorCommissions({ User_id: selectedCreator.value, Brand_id: selectedBrand?.value, includeInvoiced, reason: reason.trim() })
      .then(result => {
        const invoiceNote = result.invoiceIds.length
          ? ` Billing must reissue ${pluralizeCount(result.invoiceIds.length, 'invoice')}: ${result.invoiceIds.map(id => `#${id}`).join(', ')}.`
          : '';
        const skippedNote = result.skippedPaidOutCount
          ? ` ${pluralizeCount(result.skippedPaidOutCount, 'commission')} already paid out stayed untouched.`
          : '';
        const voidedLabel = `${pluralizeCount(result.voidedCount, 'commission')} (${formatAmountInCurrency(result.voidedTotalUsd)})`;
        const message = `Voided ${voidedLabel} for ${creatorName} ${scopeLabel} and cancelled ${pluralizeCount(
          result.cancelledPayoutCount,
          'payout'
        )}.${invoiceNote}${skippedNote}`;
        cogoToast.success(message, { hideAfter: 10 });
        setLastResult(message);
        setPreview(null);
        setReason('');
        setIncludeInvoiced(false);
      })
      .catch(err => cogoToast.error(err || 'Failed to void commissions.'))
      .finally(() => setIsVoiding(false));
  };

  const handleVoid = () => {
    if (!canVoid) return;
    const invoiceWarning = includeInvoiced && preview.invoices.length ? ` Billing must reissue ${invoiceLabels}.` : '';
    const amountLabel = formatAmountInCurrency(voidTotalUsd);
    confirmAlert({
      title: `Void ${pluralizeCount(voidCount, 'commission')} for ${preview.user.name}?`,
      message: `${amountLabel} of owed commission ${scopeLabel} will be cancelled along with the linked payouts.${invoiceWarning}`,
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        { label: 'Void', onClick: submitVoid }
      ]
    });
  };

  const isRowKept = row => row.bucket === 'paidOut' || (row.bucket === 'invoiced' && !includeInvoiced);
  const getRowOutcome = row => {
    if (row.bucket === 'paidOut') return 'Kept (already paid out)';
    if (row.bucket === 'invoiced') return includeInvoiced ? 'Void (reissue invoice)' : 'Kept (invoiced)';
    return 'Void';
  };

  return (
    <div className='void-creator-commissions manual-order-adjustments-form'>
      <div className='manual-order-adjustments-form-header'>
        <div className='manual-order-adjustments-section-title'>Void Creator Commissions</div>
      </div>
      <div className='manual-order-adjustments-csv-help'>
        Cancels every owed commission a creator has earned and the linked payouts, for banned or code-leaking creators. Commissions already paid out
        to the creator are never touched. Invoiced commissions are only voided when you include them, and billing must then reissue those invoices by
        hand.
      </div>
      <div className='manual-order-adjustments-fields'>
        <div className='manual-order-adjustments-field'>
          <label className='manual-order-adjustments-label'>
            Creator<span className='manual-order-adjustments-required'>*</span>
          </label>
          <AsyncSelect
            className='manual-order-adjustments-select'
            placeholder='Search by creator name...'
            loadOptions={searchForCreator}
            value={selectedCreator}
            onChange={selectCreator}
            isClearable
            cacheOptions
            defaultOptions={false}
            components={{ Option: SelectOption }}
          />
        </div>
        <div className='manual-order-adjustments-field'>
          <label className='manual-order-adjustments-label'>Brand</label>
          <Select
            className='manual-order-adjustments-select'
            placeholder='All brands'
            options={brandOptions}
            value={selectedBrand}
            onChange={selectBrand}
            isClearable
            isSearchable
            components={{ Option: SelectOption }}
          />
        </div>
        <div className='manual-order-adjustments-field'>
          <label className='manual-order-adjustments-label'>
            Reason<span className='manual-order-adjustments-required'>*</span>
          </label>
          <input
            className='manual-order-adjustments-input'
            type='text'
            maxLength={500}
            placeholder='e.g. Banned for fraudulent orders'
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>
      </div>
      <div className='void-creator-commissions-actions'>
        <button className='manual-order-adjustments-btn manual-order-adjustments-btn-secondary' disabled={!canPreview} onClick={handlePreview}>
          {isPreviewing ? 'Loading...' : 'Preview'}
        </button>
        {preview && (
          <button className='manual-order-adjustments-btn manual-order-adjustments-btn-primary' disabled={!canVoid} onClick={handleVoid}>
            {isVoiding ? 'Voiding...' : `Void ${pluralizeCount(voidCount, 'commission')}`}
          </button>
        )}
      </div>
      {lastResult && <div className='void-creator-commissions-result'>{lastResult}</div>}

      {preview && (
        <div className='void-creator-commissions-preview'>
          <div className='void-creator-commissions-scope'>
            {preview.user.name} ({preview.user.username}, {preview.user.id}) · {preview.user.isBanned ? 'banned' : 'not banned'} ·{' '}
            {preview.brand ? `${preview.brand.name} (${preview.brand.id})` : 'all brands'}
          </div>
          {totalCount === 0 ? (
            <div className='manual-order-adjustments-empty'>No owed commissions found.</div>
          ) : (
            <>
              <div className='void-creator-commissions-counts'>
                {countTiles.map(tile => (
                  <div key={tile.label} className={cn('void-creator-commissions-count', tile.className)}>
                    <div className='value'>{tile.count}</div>
                    <div className='label'>{`${tile.label} · ${formatAmountInCurrency(tile.totalUsd)}`}</div>
                  </div>
                ))}
              </div>
              {!!summary.invoiced.count && (
                <label className='void-creator-commissions-checkbox'>
                  <input type='checkbox' checked={includeInvoiced} onChange={e => setIncludeInvoiced(e.target.checked)} />
                  Also void the {pluralizeCount(summary.invoiced.count, 'invoiced commission')} ({formatAmountInCurrency(summary.invoiced.totalUsd)}).
                  Billing must reissue {invoiceLabels}.
                </label>
              )}
              {preview.rowsTruncated && (
                <div className='manual-order-adjustments-hint'>
                  Showing the first {preview.rows.length} commissions; the counts above cover all of them.
                </div>
              )}
              <table className='manual-order-adjustments-table'>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Brand</th>
                    <th>Order ID</th>
                    <th>Order</th>
                    <th>Commission</th>
                    <th>Commission USD</th>
                    <th>Invoice</th>
                    <th>Payout</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map(row => (
                    <tr key={row.id} className={cn({ kept: isRowKept(row) })}>
                      <td>{moment.utc(row.transaction_date).format('YYYY-MM-DD')}</td>
                      <td>{row.brandName || row.Brand_id || '—'}</td>
                      <td>{row.order_id}</td>
                      <td>{formatAmountInCurrency(row.order_amount, row.currency)}</td>
                      <td>{formatAmountInCurrency(row.commission_amount, row.currency)}</td>
                      <td>{formatAmountInCurrency(row.commission_amount_usd)}</td>
                      <td>{row.Invoice_id ? `#${row.Invoice_id} (${row.invoiceStatus || 'unknown'})` : '—'}</td>
                      <td>{row.Payout_id ? row.payoutStatus : 'none'}</td>
                      <td>{getRowOutcome(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
};

VoidCreatorCommissions.propTypes = {
  brandOptions: PropTypes.array.isRequired,
  searchForCreator: PropTypes.func.isRequired
};

export default VoidCreatorCommissions;
